import { ApplicantSettings } from "shared/app/VerificationTemplate/slices/DecisionCriteria/types";
import { CriteriaTypeOptions } from "shared/app/VerificationTemplate/slices/DecisionCriteria/constants";
import {
  RecommendationMethod,
  GroupStat
} from "types/landlord/verificationTemplate";
import { useAuthContext } from "providers/Auth";
import { Show } from "shared/components";
import { CreditScoreGraph } from "shared/app/CreditScoreGraph";
import { SingleGroupStat } from "./SingleGroupStat";

export const LegacyGroupStat = ({
  groupStat,
  criteriaSettings,
  recommendationMethod,
  criteriaTypeOptions,
  onEditEmp,
  hasIncome
}: {
  groupStat: GroupStat;
  criteriaSettings?: ApplicantSettings;
  recommendationMethod?: RecommendationMethod;
  criteriaTypeOptions: CriteriaTypeOptions;
  onEditEmp: () => void;
  hasIncome: boolean;
}) => {
  const { permissions } = useAuthContext();
  const empValue = groupStat.estimated_monthly_payments ?? 0;

  if (!hasIncome)
    return (
      <div className="flex flex-col h-full">
        <div className="flex">
          <SingleGroupStat
            noBorders
            label="Average credit score"
            valueType="credit_score"
            hasCriteria={
              criteriaTypeOptions === CriteriaTypeOptions.AVERAGE &&
              !!criteriaSettings?.credit_score?.value &&
              !!groupStat["average_credit_score"] &&
              recommendationMethod === "score_based"
            }
            criteriaSuccess={
              (groupStat["average_credit_score"] || 0) >=
              Number(criteriaSettings?.credit_score?.value || 0)
            }
            criteriaImportance={criteriaSettings?.credit_score?.importance}
            value={groupStat["average_credit_score"]}
            isPermitted={permissions.canAccessReports.creditReport}
          />
          <SingleGroupStat
            noBorders
            label="Lowest credit score"
            valueType="credit_score"
            hasCriteria={
              criteriaTypeOptions === CriteriaTypeOptions.MINIMUM &&
              !!criteriaSettings?.credit_score?.value &&
              !!groupStat["min_credit_score"] &&
              recommendationMethod === "score_based"
            }
            criteriaSuccess={
              (groupStat["min_credit_score"] || 0) >=
              Number(criteriaSettings?.credit_score?.value || 0)
            }
            criteriaImportance={criteriaSettings?.credit_score?.importance}
            value={groupStat["min_credit_score"]}
            isPermitted={permissions.canAccessReports.creditReport}
          />
          <SingleGroupStat
            noBorders
            label="Highest credit score"
            valueType="credit_score"
            hasCriteria={
              criteriaTypeOptions === CriteriaTypeOptions.MAXIMUM &&
              !!criteriaSettings?.credit_score?.value &&
              !!groupStat["max_credit_score"] &&
              recommendationMethod === "score_based"
            }
            criteriaSuccess={
              (groupStat["max_credit_score"] || 0) >=
              Number(criteriaSettings?.credit_score?.value || 0)
            }
            criteriaImportance={criteriaSettings?.credit_score?.importance}
            value={groupStat["max_credit_score"]}
            isPermitted={permissions.canAccessReports.creditReport}
          />
          <SingleGroupStat
            noBorders
            label="People with no credit score"
            valueType="credit_score"
            hasCriteria={false}
            criteriaSuccess={false}
            criteriaImportance={undefined}
            value={groupStat["no_credit_score_people"]}
            isPermitted={permissions.canAccessReports.creditReport}
          />
        </div>
        <Show if={permissions.canAccessReports.incomeReport}>
          <div className="px-6 mt-auto pb-4">
            <CreditScoreGraph
              score={
                groupStat.average_credit_score == 4
                  ? 0
                  : groupStat.average_credit_score || 0
              }
            />
          </div>
        </Show>
      </div>
    );

  return (
    <div className="grid [grid-template-columns:repeat(auto-fit,minmax(225px,1fr))] h-full">
      <SingleGroupStat
        label="Gross income"
        isVerified
        value={groupStat["gross_income"]}
        hasCriteria={
          Boolean(criteriaSettings?.gross_income) &&
          Boolean(groupStat.gross_income) &&
          recommendationMethod === "score_based"
        }
        criteriaSuccess={
          (groupStat.gross_income || 0) >=
          Number(criteriaSettings?.gross_income?.value || 0)
        }
        criteriaImportance={criteriaSettings?.gross_income?.importance}
        isPermitted={permissions.canAccessReports.incomeReport}
      />
      <SingleGroupStat
        label="Gross income to rent"
        isVerified
        valueType="ratio"
        value={groupStat["gross_income_to_rent_ratio"]}
        hasCriteria={
          !!criteriaSettings?.gross_income_to_rent_ratio &&
          !!groupStat["gross_income_to_rent_ratio"] &&
          recommendationMethod === "score_based"
        }
        criteriaSuccess={
          (groupStat["gross_income_to_rent_ratio"] || 0) >=
          Number(criteriaSettings?.gross_income_to_rent_ratio?.value || 0)
        }
        criteriaImportance={
          criteriaSettings?.gross_income_to_rent_ratio?.importance
        }
        isPermitted={permissions.canAccessReports.incomeReport}
      />
      <SingleGroupStat
        label="Debt to gross income"
        isVerified
        value={
          groupStat["debt_to_gross_income"]
            ? groupStat["debt_to_gross_income"] * 100
            : groupStat["debt_to_gross_income"]
        }
        valueType="percentage"
        hasCriteria={
          !!criteriaSettings?.debt_to_gross_income &&
          !!groupStat["debt_to_gross_income"] &&
          recommendationMethod === "score_based"
        }
        criteriaSuccess={
          (groupStat["debt_to_gross_income"] || 0) <=
          Number(
            String(criteriaSettings?.debt_to_gross_income?.value).replace(
              "%",
              ""
            ) || 0
          )
        }
        criteriaImportance={criteriaSettings?.debt_to_gross_income?.importance}
        isPermitted={permissions.canAccessReports.incomeReport}
        onEditEmp={onEditEmp}
        empValue={empValue}
      />
      <SingleGroupStat
        label="Net income"
        isVerified
        value={groupStat["net_income"]}
        hasCriteria={
          !!criteriaSettings?.net_income &&
          !!groupStat["net_income"] &&
          recommendationMethod === "score_based"
        }
        criteriaSuccess={
          (groupStat["net_income"] || 0) >=
          Number(criteriaSettings?.net_income?.value || 0)
        }
        criteriaImportance={criteriaSettings?.net_income?.importance}
        isPermitted={permissions.canAccessReports.incomeReport}
      />
      <SingleGroupStat
        label="Debt to net income"
        isVerified
        value={
          groupStat["debt_to_net_income"]
            ? groupStat["debt_to_net_income"] * 100
            : groupStat["debt_to_net_income"]
        }
        valueType="percentage"
        hasCriteria={
          !!criteriaSettings?.debt_to_net_income &&
          !!groupStat["debt_to_net_income"] &&
          recommendationMethod === "score_based"
        }
        criteriaSuccess={
          (groupStat["debt_to_net_income"] || 0) <=
          Number(
            String(criteriaSettings?.debt_to_net_income?.value).replace(
              "%",
              ""
            ) || 0
          )
        }
        criteriaImportance={criteriaSettings?.debt_to_net_income?.importance}
        isPermitted={permissions.canAccessReports.incomeReport}
        onEditEmp={onEditEmp}
        empValue={empValue}
      />
      <SingleGroupStat
        label="Highest credit score"
        valueType="credit_score"
        hasCriteria={false}
        criteriaSuccess={false}
        criteriaImportance={undefined}
        value={groupStat["max_credit_score"]}
        isPermitted={permissions.canAccessReports.creditReport}
      />
      <SingleGroupStat
        label="Gross income"
        isSelfAttested
        value={groupStat["self_reported_gross_income"]}
        isPermitted={permissions.canAccessReports.incomeReport}
      />
      <SingleGroupStat
        label="Gross income to rent"
        isSelfAttested
        valueType="ratio"
        value={groupStat["self_reported_gross_income_to_rent"]}
        isPermitted={permissions.canAccessReports.incomeReport}
      />
      <SingleGroupStat
        label="Debt to gross income"
        isSelfAttested
        value={
          groupStat["debt_to_self_reported_gross_income"]
            ? groupStat["debt_to_self_reported_gross_income"] * 100
            : groupStat["debt_to_self_reported_gross_income"]
        }
        valueType="percentage"
        hasCriteria={
          !!criteriaSettings?.debt_to_self_reported_gross_income &&
          !!groupStat["debt_to_self_reported_gross_income"] &&
          recommendationMethod === "score_based"
        }
        criteriaSuccess={
          (groupStat["debt_to_self_reported_gross_income"] || 0) <=
          Number(
            String(
              criteriaSettings?.debt_to_self_reported_gross_income?.value
            ).replace("%", "") || 0
          )
        }
        criteriaImportance={
          criteriaSettings?.debt_to_self_reported_gross_income?.importance
        }
        isPermitted={permissions.canAccessReports.incomeReport}
      />
      <SingleGroupStat
        label="People with no credit score"
        valueType="credit_score"
        hasCriteria={false}
        criteriaSuccess={false}
        criteriaImportance={undefined}
        value={groupStat["no_credit_score_people"]}
        isPermitted={permissions.canAccessReports.creditReport}
      />
      <SingleGroupStat
        label="Average credit score"
        valueType="credit_score"
        hasCriteria={
          criteriaTypeOptions === CriteriaTypeOptions.AVERAGE &&
          !!criteriaSettings?.credit_score &&
          !!groupStat["average_credit_score"] &&
          recommendationMethod === "score_based"
        }
        criteriaSuccess={
          (groupStat["average_credit_score"] || 0) >=
            Number(criteriaSettings?.credit_score?.value || 0) &&
          recommendationMethod === "score_based"
        }
        criteriaImportance={criteriaSettings?.credit_score?.importance}
        value={
          groupStat["average_credit_score"] == 4
            ? undefined
            : groupStat["average_credit_score"]
        }
        isPermitted={permissions.canAccessReports.creditReport}
      />
      <SingleGroupStat
        label="Lowest credit score"
        valueType="credit_score"
        hasCriteria={false}
        criteriaSuccess={false}
        criteriaImportance={undefined}
        value={groupStat["min_credit_score"]}
        isPermitted={permissions.canAccessReports.creditReport}
      />
    </div>
  );
};
